import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "../components/seo/SEO";
import { Section, Fade, Heading, Label, Text, Button, C } from "../components/ui";
import { CASES } from "../data";
import type { CaseStudy } from "../data";

export default function CaseStudies() {
  const navigate = useNavigate();

  // Create structured data for SEO
  const casesStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Case Studies",
    description: "Real results for local businesses",
    numberOfItems: CASES.length,
    itemListElement: CASES.map((c, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "CaseStudy",
        name: c.title,
        description: c.prob,
        industry: c.tag.split("·")[0].trim(),
        location: c.tag.split("·")[1]?.trim() || "",
        results: c.res,
      },
    })),
  };

  const goToContact = () => {
    navigate("/contact");
  };

  return (
    <>
      <SEO
        title="Case Studies"
        description="See real results from local businesses we've helped. From restaurants to salons, discover how RevSpike delivers measurable growth with done-for-you marketing."
        structuredData={casesStructuredData}
      />

      {/* Hero Section */}
      <Section background={C.navy}>
        <Fade>
          <div style={{ textAlign: "center", maxWidth: "800px", margin: "0 auto" }}>
            <Label light>Case Studies</Label>
            <Heading light style={{ marginBottom: "24px" }}>
              Real Results for Local Businesses
            </Heading>
            <Text light size="lg" style={{ marginBottom: "40px" }}>
              See how we've helped restaurants, salons, and local businesses dominate
              their markets with done-for-you marketing.
            </Text>
            <Button variant="coral" onClick={goToContact}>
              Get Results Like These
            </Button>
          </div>
        </Fade>
      </Section>

      {/* Case Studies Grid */}
      <Section background={C.offWhite}>
        <Fade>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <Label>Our Work</Label>
            <Heading size={2}>Featured Success Stories</Heading>
          </div>
        </Fade>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "32px",
            marginTop: "48px",
          }}
        >
          {CASES.map((caseStudy: CaseStudy, index: number) => (
            <Fade key={index} delay={index * 0.1}>
              <CaseStudyCard caseStudy={caseStudy} />
            </Fade>
          ))}
        </div>
      </Section>

      {/* CTA Section */}
      <Section background={C.navyLight}>
        <Fade>
          <div style={{ textAlign: "center", maxWidth: "700px", margin: "0 auto" }}>
            <Heading light style={{ marginBottom: "24px" }}>
              Ready to See Results Like These?
            </Heading>
            <Text light size="lg" style={{ marginBottom: "40px" }}>
              Let's build a growth strategy for your business. Free consultation, no
              obligation.
            </Text>
            <Button variant="coral" size="lg" onClick={goToContact}>
              Start Your Growth Journey
            </Button>
          </div>
        </Fade>
      </Section>
    </>
  );
}

interface CaseStudyCardProps {
  caseStudy: CaseStudy;
}

function CaseStudyCard({ caseStudy }: CaseStudyCardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: C.white,
        borderRadius: "12px",
        padding: "32px",
        boxShadow: hovered
          ? "0 20px 40px rgba(10, 25, 41, 0.1)"
          : "0 4px 12px rgba(10, 25, 41, 0.08)",
        transition: "all 0.3s ease",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Tag */}
      <div style={{ marginBottom: "20px" }}>
        <span
          style={{
            fontFamily: C.mono,
            fontSize: "11px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: C.coral,
            fontWeight: 600,
          }}
        >
          {caseStudy.tag}
        </span>
      </div>

      {/* Title */}
      <Heading
        size={3}
        style={{
          marginBottom: "16px",
          fontSize: "1.35rem",
          lineHeight: 1.3,
        }}
      >
        {caseStudy.title}
      </Heading>

      {/* Problem */}
      <div style={{ marginBottom: "20px", flex: 1 }}>
        <Text size="sm" style={{ color: C.gray600, marginBottom: "8px" }}>
          <strong style={{ color: C.navy }}>Challenge:</strong> {caseStudy.prob}
        </Text>
      </div>

      {/* Solution */}
      <div style={{ marginBottom: "20px", flex: 1 }}>
        <Text size="sm" style={{ color: C.gray600, marginBottom: "8px" }}>
          <strong style={{ color: C.navy }}>Solution:</strong> {caseStudy.sol}
        </Text>
      </div>

      {/* Results */}
      <div style={{ marginBottom: "24px" }}>
        <Text size="sm" style={{ color: C.gray600, fontWeight: 600, marginBottom: "12px" }}>
          RESULTS:
        </Text>
        <ul
          style={{
            margin: 0,
            paddingLeft: "20px",
            listStyle: "none",
          }}
        >
          {caseStudy.res.slice(0, 3).map((result, idx) => (
            <li
              key={idx}
              style={{
                position: "relative",
                marginBottom: "8px",
                fontSize: "0.9rem",
                color: C.gray500,
                lineHeight: 1.6,
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: "-20px",
                  color: C.coral,
                  fontWeight: "bold",
                }}
              >
                ✓
              </span>
              {result}
            </li>
          ))}
        </ul>
      </div>

      {/* Package Badge */}
      <div
        style={{
          display: "inline-block",
          padding: "6px 12px",
          background: C.gray50,
          borderRadius: "4px",
          border: `1px solid ${C.gray200}`,
          alignSelf: "flex-start",
          marginBottom: "16px",
        }}
      >
        <Text size="sm" style={{ color: C.gray600, margin: 0 }}>
          Package: <strong>{caseStudy.pkg}</strong>
        </Text>
      </div>

      {/* CTA */}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => console.log("View case study:", caseStudy.title)}
        style={{ width: "100%" }}
      >
        View Full Case Study
      </Button>
    </div>
  );
}
